import type {
    AttachmentType,
    Chat,
    ChatNotificationLevel,
    FileAttachment,
    Message,
    Reaction,
    User,
} from '../Contracts'
import { attachmentPreview } from './attachmentPreview'
import { stripHtml } from './stripHtml'

/**
 * Translation layer between the autostrada backend and the chat contracts.
 *
 * The backend speaks its own dialect (see /services/chat/* responses):
 *   - entities are addressed by `uuid`, the UI expects `id`;
 *   - dates come as ISO-8601 strings (DATE_ATOM), the UI expects Unix ms;
 *   - the serializer STRIPS null fields from responses (removeNulls), so
 *     every absent optional must be restored here to its contract default;
 *   - participants come as ChatParticipant rows with an embedded user,
 *     the UI expects plain `participantIds: string[]`.
 */

/**
 * Deeply nested entities get collapsed by the backend serializer (MaxDepth)
 * to a bare reference: { "@id": "/services/users/<uuid>", "@type": "User" }.
 * Every mapper must therefore resolve ids via refUuid(), which understands
 * both a full object and a collapsed reference.
 */
interface ApiEntityRef {
    uuid?: string
    '@id'?: string
}

/** User as serialized with `user:short:read` (also embedded in participants) */
export interface ApiUser extends ApiEntityRef {
    firstName?: string | null
    lastName?: string | null
    photo?: { url?: string | null } | null
    lastActivityAt?: string | null
}

interface ApiParticipant {
    user?: ApiUser
}

/** Aggregated reaction group as the backend sends it */
export interface ApiReaction {
    emoji?: string
    userUuids?: string[]
}

/** Attachment as presented by the backend (ChatFilePresenter::present) */
export interface ApiFile {
    uuid?: string
    type?: string
    name?: string | null
    size?: number
    url?: string | null
}

/** Message as serialized with `message:*read` groups */
export interface ApiMessage extends ApiEntityRef {
    chat?: ApiEntityRef
    sender?: ApiUser | null
    content?: string | null
    replyTo?: ApiEntityRef | null
    isEdited?: boolean
    isDeleted?: boolean
    createdAt?: string | null
    reactions?: ApiReaction[] | null
    files?: ApiFile[] | null
}

/** Read state of one participant as the backend sends it */
export interface ApiReadState {
    userUuid?: string
    lastReadAt?: string | null
}

/** Chat as serialized with `chat:*read` groups */
export interface ApiChat {
    uuid?: string
    type?: string
    name?: string | null
    entityType?: string | null
    entityUuid?: string | null
    participants?: ApiParticipant[]
    lastMessage?: ApiMessage | null
    unreadCount?: number
    myNotificationLevel?: string | null
    readStates?: ApiReadState[] | null
    createdBy?: ApiUser | null
    avatarUrl?: string | null
    updatedAt?: string | null
    createdAt?: string | null
}

/** Payload of the `message:new` Centrifugo event (built by ChatEventPublisher) */
export interface RealtimeMessagePayload {
    uuid?: string
    chatUuid?: string
    senderUuid?: string | null
    senderName?: string | null
    content?: string | null
    replyToUuid?: string | null
    isEdited?: boolean
    isDeleted?: boolean
    createdAt?: string | null
    /** Uuids of mentioned participants (backend sends since phase 2 files/mentions step) */
    mentionedUserUuids?: string[]
    /** Attachments of the message (present only when it has any) */
    files?: ApiFile[]
}

/** Payload of the `message:delivery` event: a user has read the chat up to lastReadAt */
export interface RealtimeDeliveryPayload {
    chatUuid?: string
    userUuid?: string
    lastReadAt?: string | null
}

/** Payload of the `typing:start` event (no stop event — TTL on the client) */
export interface RealtimeTypingPayload {
    chatUuid?: string
    userUuid?: string
    firstName?: string | null
}

/** Payload of the `chat:new` Centrifugo event */
export interface RealtimeChatPayload {
    uuid?: string
    type?: string
    name?: string | null
    entityType?: string | null
    entityUuid?: string | null
    createdByUuid?: string | null
    participantUuids?: string[]
    updatedAt?: string | null
}

/** ISO-8601 (DATE_ATOM) → Unix ms; garbage or absence → 0 */
export const toTimestamp = (iso?: string | null): number => {
    if (!iso) return 0
    const ms = Date.parse(iso)
    return Number.isNaN(ms) ? 0 : ms
}

/** Validates a raw level against the contract; unknown values degrade to 'all' */
export const toNotificationLevel = (value?: string | null): ChatNotificationLevel =>
    value === 'badge' || value === 'muted' ? value : 'all'

/** Backend aggregate {emoji, userUuids} → contract Reaction {emoji, userIds} */
export const mapApiReactions = (list?: ApiReaction[] | null): Reaction[] =>
    (list ?? []).map((item) => ({
        emoji: item.emoji ?? '',
        userIds: item.userUuids ?? [],
    }))

/** Validates a raw attachment type; unknown values degrade to 'file' */
const toAttachmentType = (value?: string | null): AttachmentType =>
    value === 'photo' || value === 'video' ? value : 'file'

/** Backend attachment {uuid, type, name, size, url} → contract FileAttachment */
export const mapApiFiles = (list?: ApiFile[] | null): FileAttachment[] =>
    (list ?? []).map((item) => ({
        type: toAttachmentType(item.type),
        name: item.name ?? '',
        size: item.size ?? 0,
        url: item.url ?? '',
    }))

/**
 * Uuid of a related entity: from the `uuid` field when the entity is
 * serialized in full, or from the last segment of the `@id` IRI when the
 * serializer collapsed it to a reference (uuid is the public identifier
 * of every chat-related resource, so the IRI always ends with it).
 */
const refUuid = (ref?: ApiEntityRef | null): string => {
    if (!ref) return ''
    if (ref.uuid) return ref.uuid
    return ref['@id']?.split('/').pop() ?? ''
}

export const mapApiUser = (api: ApiUser): User => ({
    id: api.uuid ?? '',
    firstName: api.firstName ?? '',
    lastName: api.lastName ?? '',
    avatarUrl: api.photo?.url ?? '',
    // Реальний статус — похідна від presence-каналу Centrifugo: його
    // виставляє UserStore (onlineUuids) при upsert і join/leave подіях.
    // lastSeen (з last_activity_at) лишається для підпису «був(ла) …»
    status: 'offline',
    lastSeen: toTimestamp(api.lastActivityAt),
})

/**
 * Users embedded in chat participants — used to hydrate the UserStore.
 * Collapsed references (uuid known, but no name) are skipped: upserting
 * them would overwrite good contact entries with empty ones.
 */
export const extractChatUsers = (api: ApiChat): User[] =>
    (api.participants ?? [])
        .map((participant) => participant.user)
        .filter((user): user is ApiUser => Boolean(user?.uuid && user.firstName))
        .map(mapApiUser)

export const mapApiChat = (api: ApiChat): Chat => {
    const last = api.lastMessage

    return {
        id: api.uuid ?? '',
        type: api.type === 'group' ? 'group' : 'private',
        participantIds: (api.participants ?? [])
            .map((participant) => refUuid(participant.user))
            .filter(Boolean),
        // exactOptionalPropertyTypes: an optional key must be absent, not undefined
        ...(api.name != null ? { name: api.name } : {}),
        lastMessage: last
            ? {
                  // Content is Tiptap HTML — the list preview needs plain
                  // text; no text at all means a file-only message
                  text: stripHtml(last.content ?? '') || attachmentPreview(mapApiFiles(last.files)),
                  senderId: refUuid(last.sender),
                  timestamp: toTimestamp(last.createdAt),
              }
            : null,
        unreadCount: api.unreadCount ?? 0,
        updatedAt: toTimestamp(api.updatedAt ?? api.createdAt),
        notificationLevel: toNotificationLevel(api.myNotificationLevel),
        ...(api.entityType ? { entityType: api.entityType } : {}),
        ...(api.entityUuid ? { entityUuid: api.entityUuid } : {}),
        ...(refUuid(api.createdBy) ? { ownerId: refUuid(api.createdBy) } : {}),
        ...(api.avatarUrl ? { avatarUrl: api.avatarUrl } : {}),
        ...(api.readStates?.length
            ? {
                  readStates: api.readStates.map((state) => ({
                      userId: state.userUuid ?? '',
                      lastReadAt: toTimestamp(state.lastReadAt),
                  })),
              }
            : {}),
    }
}

/**
 * @param fallbackChatId chat uuid from the request context — the serializer
 *        may collapse `message.chat` to a reference without fields
 */
export const mapApiMessage = (api: ApiMessage, fallbackChatId = ''): Message => ({
    id: api.uuid ?? '',
    chatId: refUuid(api.chat) || fallbackChatId,
    senderId: refUuid(api.sender),
    content: api.content ?? '',
    timestamp: toTimestamp(api.createdAt),
    isEdited: api.isEdited ?? false,
    isDeleted: api.isDeleted ?? false,
    ...(refUuid(api.replyTo) ? { replyToId: refUuid(api.replyTo) } : {}),
    ...(api.reactions?.length ? { reactions: mapApiReactions(api.reactions) } : {}),
    ...(api.files?.length ? { files: mapApiFiles(api.files) } : {}),
})

export const mapRealtimeMessage = (payload: RealtimeMessagePayload): Message => ({
    id: payload.uuid ?? '',
    chatId: payload.chatUuid ?? '',
    senderId: payload.senderUuid ?? '',
    content: payload.content ?? '',
    timestamp: toTimestamp(payload.createdAt),
    isEdited: payload.isEdited ?? false,
    isDeleted: payload.isDeleted ?? false,
    ...(payload.replyToUuid ? { replyToId: payload.replyToUuid } : {}),
    ...(payload.files?.length ? { files: mapApiFiles(payload.files) } : {}),
})

export const mapRealtimeChat = (payload: RealtimeChatPayload): Chat => ({
    id: payload.uuid ?? '',
    type: payload.type === 'group' ? 'group' : 'private',
    participantIds: payload.participantUuids ?? [],
    ...(payload.createdByUuid ? { ownerId: payload.createdByUuid } : {}),
    ...(payload.name != null ? { name: payload.name } : {}),
    lastMessage: null,
    unreadCount: 0,
    updatedAt: toTimestamp(payload.updatedAt),
    notificationLevel: 'all',
})
